/**
 * Pull-to-refresh — drag down from the top of the list to HARD-reload the app
 * (fresh code + fresh data, cache bypassed).
 *
 * Works on the list's own scroll container (the react-window list INSIDE the
 * wrapped area — found from the touch target, not by walking up from the
 * host): the gesture only arms when that element is already at the very top
 * at touch-down and is not still coasting from a fling, and it disarms the
 * moment the list scrolls or the finger moves up, so normal scrolling is
 * untouched. Touch-only (native + mobile web). Never calls preventDefault.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { requestSyncNow } from '../lib/repo';

/** Drag distance (px) that triggers the refresh. */
const THRESHOLD = 72;
/** Max visual travel of the indicator. */
const MAX_PULL = 96;
/** Upward finger travel (px) tolerated before the gesture is abandoned. */
const UP_SLOP = 4;
/** A scroll event this recent (ms) means the list is still moving (fling). */
const SETTLE_MS = 150;

type Scroller = HTMLElement | Window;

function isScrollable(el: HTMLElement): boolean {
  const style = window.getComputedStyle(el);
  return /(auto|scroll)/.test(style.overflowY) && el.scrollHeight > el.clientHeight;
}

/**
 * The element whose scroll position decides whether we're "at the top":
 * 1. the nearest scrollable element from the touch target up to the host;
 * 2. else the first scrollable descendant of the host (e.g. the touch landed
 *    on an overlay or the gap around the list);
 * 3. else the nearest scrollable ancestor of the host;
 * 4. else the window.
 */
function findScroller(target: EventTarget | null, host: HTMLElement | null): Scroller {
  if (!host) return window;
  let el: HTMLElement | null = target instanceof HTMLElement ? target : null;
  while (el && host.contains(el)) {
    if (isScrollable(el)) return el;
    if (el === host) break;
    el = el.parentElement;
  }
  for (const node of Array.from(host.querySelectorAll<HTMLElement>('*'))) {
    if (isScrollable(node)) return node;
  }
  el = host.parentElement;
  while (el) {
    if (isScrollable(el)) return el;
    el = el.parentElement;
  }
  return window;
}

function scrollTopOf(target: Scroller): number {
  return target instanceof Window ? window.scrollY : target.scrollTop;
}

export function PullToRefresh({ children }: { children: ReactNode }): JSX.Element {
  const hostRef = useRef<HTMLDivElement>(null);
  const startYRef = useRef<number | null>(null);
  const scrollerRef = useRef<Scroller | null>(null);
  const pullRef = useRef(0);
  const lastScrollAtRef = useRef(0);
  const [pull, setPullState] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const setPull = (v: number): void => {
    pullRef.current = v;
    setPullState(v);
  };

  // Track scroll activity anywhere inside the host (scroll doesn't bubble, so
  // listen in the capture phase). Lets us refuse to arm while a fling is
  // still decelerating — including the iOS bounce back to 0.
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const onScroll = (): void => {
      lastScrollAtRef.current = performance.now();
    };
    host.addEventListener('scroll', onScroll, { capture: true, passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      host.removeEventListener('scroll', onScroll, { capture: true });
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  const disarm = (): void => {
    startYRef.current = null;
    scrollerRef.current = null;
    if (pullRef.current !== 0) setPull(0);
  };

  const onTouchStart = (e: React.TouchEvent): void => {
    startYRef.current = null;
    scrollerRef.current = null;
    if (refreshing || e.touches.length !== 1) return;
    const scroller = findScroller(e.target, hostRef.current);
    const atTop = scrollTopOf(scroller) <= 0;
    const settled = performance.now() - lastScrollAtRef.current > SETTLE_MS;
    if (!atTop || !settled) return;
    scrollerRef.current = scroller;
    startYRef.current = e.touches[0].clientY;
  };

  const onTouchMove = (e: React.TouchEvent): void => {
    if (startYRef.current === null || refreshing) return;
    if (e.touches.length !== 1) {
      disarm();
      return;
    }
    // The list started scrolling (finger moved up, or it moved on its own) —
    // this is a normal scroll, not a pull. Give up for the rest of the touch.
    const scroller = scrollerRef.current;
    if (scroller && scrollTopOf(scroller) > 0) {
      disarm();
      return;
    }
    const delta = e.touches[0].clientY - startYRef.current;
    if (delta < -UP_SLOP) {
      disarm();
      return;
    }
    if (delta <= 0) {
      if (pullRef.current !== 0) setPull(0);
      return;
    }
    // Resistance: the further you pull, the slower it moves.
    setPull(Math.min(MAX_PULL, delta * 0.55));
  };

  const onTouchEnd = (): void => {
    if (startYRef.current === null) return;
    startYRef.current = null;
    scrollerRef.current = null;
    if (pullRef.current >= THRESHOLD * 0.55) {
      setRefreshing(true);
      setPull(MAX_PULL * 0.6);
      // Ask the server to re-read the spreadsheet FIRST (so the reload shows the
      // edit that was just made, not the last scheduled pass), then hard-reload.
      // Capped so a slow network can't leave the spinner up for long.
      void Promise.race([
        requestSyncNow(),
        new Promise((res) => setTimeout(res, 6000)),
      ]).finally(() => window.location.reload());
      return;
    }
    setPull(0);
  };

  const armed = pull >= THRESHOLD * 0.55;

  return (
    <div
      ref={hostRef}
      className="relative flex min-h-0 flex-1 flex-col"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchEnd}
    >
      <div
        className="pointer-events-none absolute inset-x-0 top-0 z-20 flex justify-center overflow-hidden transition-[height] duration-100"
        style={{ height: pull }}
        aria-hidden={pull === 0}
      >
        <div className="flex items-end pb-1">
          <span
            className={`flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-[11px] font-semibold shadow-mini ${
              armed || refreshing ? 'text-brand' : 'text-slate-400'
            }`}
          >
            <span
              className={`inline-block h-3 w-3 rounded-full border-2 border-current border-t-transparent ${
                refreshing ? 'animate-spin' : ''
              }`}
              style={refreshing ? undefined : { transform: `rotate(${pull * 4}deg)` }}
            />
            {refreshing ? 'Refreshing…' : armed ? 'Release to refresh' : 'Pull to refresh'}
          </span>
        </div>
      </div>

      <div
        className="flex min-h-0 flex-1 flex-col transition-transform duration-100"
        style={{ transform: pull > 0 ? `translateY(${pull}px)` : undefined }}
      >
        {children}
      </div>
    </div>
  );
}
